/**
 * Photo uploads, owned outside React.
 *
 * They used to live inside CameraCaptureModal, which forced an ugly compromise:
 * the modal had to refuse to close while anything was in flight, or the uploads
 * would die with the component. So a technician who shot ten photos was held at
 * a progress bar until a strictly serial queue drained — the "everything goes
 * green at the end, then you can leave" complaint.
 *
 * Living here, the queue survives the modal closing, so the camera can be
 * dismissed the instant the last shot is taken and the photos keep going up in
 * the background. Nothing is dropped: the queue still holds every file until the
 * server has acknowledged it, and anything unfinished triggers the browser's
 * "leave site?" prompt (see hasPendingUploads).
 */
import { apiUrl } from '../config/api';
import * as store from './uploadStore';
import { uploadVideo, abandonVideo } from './videoUpload';

// Parallel uploads once the project is known. Three keeps a phone's uplink busy
// without starving the rest of the app on a weak connection.
const MAX_CONCURRENT = 3;

// kind is 'photo' or 'video'; a video also carries posterBlob, durationMs and progress (0..1).
const items = [];                 // { id, batchId, kind, file, preview, status, error, ... }
const batches = new Map();        // batchId -> { clientId, clientName, clientAddress, inspectionDate, token }
const listeners = new Set();
let running = 0;

function emit() {
  const snapshot = items.slice();
  listeners.forEach((fn) => fn(snapshot));
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(items.slice());
  return () => listeners.delete(fn);
}

export function getItems(batchId) {
  return batchId ? items.filter((i) => i.batchId === batchId) : items.slice();
}

/** Anything still queued, uploading, or failed and therefore not yet safe to lose. */
export function hasPendingUploads() {
  return items.some((i) => i.status === 'pending' || i.status === 'uploading' || i.status === 'failed');
}

export function countsFor(batchId) {
  return getItems(batchId).reduce(
    (acc, i) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; },
    { pending: 0, uploading: 0, done: 0, failed: 0 }
  );
}

/** Starts (or updates) a batch — one project's worth of photos. */
export function startBatch(batchId, meta) {
  const existing = batches.get(batchId) || {};
  // A clientId learned from the first upload must survive later updates. The modal
  // re-announces the batch on every capture, and for a brand-new project it has no
  // id to announce — blindly merging put the batch back into "project unknown",
  // which pinned uploads to one at a time for the whole session.
  const clientId = meta.clientId || existing.clientId || '';
  batches.set(batchId, { ...existing, ...meta, clientId });
}

export function addPhoto({ batchId, id, file, preview, kind = 'photo', posterBlob = null, durationMs = null }) {
  items.push({ id, batchId, kind, file, preview, posterBlob, durationMs, progress: 0, status: 'pending', error: null });
  emit();
  // Written before the upload is attempted, so a photo is recoverable from the
  // instant it exists rather than only once an upload has failed.
  const meta = batches.get(batchId) || {};
  store.put({
    id,
    batchId,
    kind,
    file,
    preview,
    posterBlob,
    durationMs,
    // The token is not persisted: it expires, and a stale credential sitting in
    // browser storage is worth less than reading the live one at resume time.
    meta: {
      clientId: meta.clientId || '',
      clientName: meta.clientName || '',
      clientAddress: meta.clientAddress || '',
      inspectionDate: meta.inspectionDate || '',
    },
  });
  pump();
}

/**
 * Picks up where a killed app left off. Called once at startup; the live auth
 * token is passed in because the stored record deliberately has none.
 */
export async function resumePending(token) {
  if (!token) return 0;
  const saved = await store.all();
  if (!saved.length) return 0;

  let added = 0;
  for (const rec of saved) {
    if (items.some((i) => i.id === rec.id)) continue; // already in this session
    batches.set(rec.batchId, { ...(batches.get(rec.batchId) || {}), ...rec.meta, token });
    items.push({
      id: rec.id,
      batchId: rec.batchId,
      kind: rec.kind || 'photo',
      file: rec.file,
      preview: rec.preview,
      posterBlob: rec.posterBlob || null,
      durationMs: rec.durationMs || null,
      progress: 0,
      status: 'pending',
      error: null,
      resumed: true,
    });
    added += 1;
  }
  if (added) { emit(); pump(); }
  return added;
}

/** Photos recovered from a previous session that are still going up. */
export function resumedCount() {
  return items.filter((i) => i.resumed && (i.status === 'pending' || i.status === 'uploading')).length;
}

export function retryFailed(batchId) {
  let changed = false;
  for (const item of items) {
    if (item.batchId === batchId && item.status === 'failed') {
      item.status = 'pending';
      item.error = null;
      changed = true;
    }
  }
  if (changed) { emit(); pump(); }
}

export function discard(id) {
  const i = items.findIndex((x) => x.id === id);
  // Only a failed photo may be thrown away; anything else is in flight or safe.
  if (i >= 0 && items[i].status === 'failed') {
    const [item] = items.splice(i, 1);
    store.remove(id);
    // A video may have parts sitting in S3 already; let it drop them.
    if (item.kind === 'video') abandonVideo(id, batches.get(item.batchId)?.token);
    emit();
  }
}

/** Forget a finished batch so the list doesn't grow for the life of the session. */
export function clearCompleted(batchId) {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    if (items[i].batchId === batchId && items[i].status === 'done') items.splice(i, 1);
  }
  emit();
}

async function sendVideo(item, meta) {
  let lastEmit = 0;
  await uploadVideo({
    id: item.id,
    file: item.file,
    posterBlob: item.posterBlob,
    durationMs: item.durationMs,
    target: meta,
    token: meta.token,
    // The project exists as soon as the upload starts, so later items can go in
    // parallel without waiting for the whole video.
    onClientId: (clientId) => {
      const current = batches.get(item.batchId);
      if (clientId && current && !current.clientId) {
        batches.set(item.batchId, { ...current, clientId });
        pump();
      }
    },
    onProgress: (fraction) => {
      item.progress = fraction;
      const now = Date.now();
      if (now - lastEmit > 250 || fraction >= 1) { lastEmit = now; emit(); }
    },
  });
}

async function send(item) {
  const meta = batches.get(item.batchId);
  if (!meta) throw new Error('missing batch');
  if (item.kind === 'video') { await sendVideo(item, meta); return; }

  const form = new FormData();
  form.append('image', item.file);
  form.append('clientId', meta.clientId || '');
  form.append('clientName', meta.clientName || '');
  form.append('clientAddress', meta.clientAddress || '');
  form.append('inspectionDate', meta.inspectionDate || '');
  const res = await fetch(apiUrl('/api/scraper/upload-image'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${meta.token}` },
    body: form,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const data = await res.json().catch(() => ({}));
  // The first upload of a brand-new project creates it. Recording the id here is
  // what lets every later photo go up in parallel instead of racing to create
  // duplicate projects.
  if (data.clientId && !meta.clientId) {
    batches.set(item.batchId, { ...meta, clientId: data.clientId });
  }
}

function pump() {
  if (running >= MAX_CONCURRENT) return;

  const next = items.find((i) => i.status === 'pending');
  if (!next) return;

  const meta = batches.get(next.batchId);
  // Until the project exists, exactly one upload may run — it is the one that
  // creates it. Letting two go would create two projects for the same job.
  const projectUnknown = !meta?.clientId;
  if (projectUnknown && running > 0) return;

  next.status = 'uploading';
  running += 1;
  emit();

  send(next)
    .then(() => {
      next.status = 'done';
      next.error = null;
      // Only now is the photo safe to forget: the server has it.
      store.remove(next.id);
    })
    .catch((err) => { next.status = 'failed'; next.error = err?.message || 'Upload failed'; })
    .finally(() => {
      running -= 1;
      emit();
      pump();           // start the next one
      if (running < MAX_CONCURRENT) pump(); // and fill any spare slot
    });

  if (!projectUnknown) pump(); // project known: saturate the remaining slots now
}
